import React, { useCallback } from 'react'
import { Box, CircularProgress, DialogContent, Typography } from '@mui/material'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'

import SearchResultUserCard from '@components/check-in-widget/search-result-user-card/search-result-user-card.component'
import { useClients } from '@components/check-in-widget/search/search.hooks'
import { PAGE_SIZE } from '@components/check-in-widget/modal-check-in/modal-check-in.constants'
import Button from '@hapana/shared/web/components/shared/button/button.component'
import {
  BUTTON_SIZE,
  BUTTON_THEME,
  BUTTON_VARIANTS,
} from '@hapana/shared/web/components/shared/button/button.constants'
import AngleDownIcon from '@hapana/shared/web/styles/assets/svgs/angle-down-small.svg'
import EmptySearch from '@components/check-in-widget/manual-check-in/empty-search.component'
import {
  useInitiateCheckIn,
  useInitiateTanningCheckIn,
} from '@components/check-in-widget/search-result-user-card/initiate-checkin.hooks'
import { useErrorHandler } from '@hapana/shared/web/hooks/errors.hooks'
import { GENERIC_ERROR_KEY } from '@hapana/shared/constants/errors.constants'
import { getClients } from '@services/check-in/clients.service'
import { getCheckIns } from '@services/check-in/check-in.service'

import { styles } from './manual-check-in.styles'

const ManualCheckIn = ({ searchValue, onManualCheckin, isTanning }) => {
  const { t } = useTranslation(['checkIn'])
  const { onError } = useErrorHandler(GENERIC_ERROR_KEY)
  const queryClient = useQueryClient()
  const { isLoading: loadingRegularCheckIn, mutate: mutateRegular } = useInitiateCheckIn({
    onSuccess: (response) => {
      // Invalidate clients cache so lastCheckInOn reflects the new check-in on the next search
      queryClient.invalidateQueries(getClients.key)
      queryClient.invalidateQueries(getCheckIns.key)
      onManualCheckin(response?.createCheckIn)
    },
    onError,
  })
  const { isLoading: loadingTanningCheckIn, mutate: mutateTanning } = useInitiateTanningCheckIn({
    onSuccess: (response) => {
      queryClient.invalidateQueries(getClients.key)
      queryClient.invalidateQueries(getCheckIns.key)
      onManualCheckin(response?.createTanningCheckIn)
    },
    onError,
  })
  const mutate = isTanning ? mutateTanning : mutateRegular
  const loadingManualCheckIn = isTanning ? loadingTanningCheckIn : loadingRegularCheckIn
  const handleManualCheckin = useCallback(
    (clientId, siteId) => {
      mutate({
        clientId,
        siteId,
      })
    },
    [mutate]
  )

  const {
    isLoading,
    isError,
    data,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    refetch,
    isRefetching,
  } = useClients({
    search: searchValue,
    paginate: {
      pageSize: PAGE_SIZE,
    },
  })
  const isRefetchingInBackground = isRefetching && !isFetchingNextPage

  if (isLoading) {
    return (
      <DialogContent sx={styles.dialogContent}>
        <Box sx={styles.loading}>
          <CircularProgress size={24} />
        </Box>
      </DialogContent>
    )
  }
  const pages = data?.pages
  const clients = pages?.flatMap((item) => item.clients.results)

  const loadMore = () => {
    if (isError) {
      return refetch()
    }
    return fetchNextPage()
  }
  if (clients?.length === 0) {
    return <EmptySearch isLoading={isLoading} />
  }

  if (loadingManualCheckIn) {
    return (
      <DialogContent sx={styles.dialogContent}>
        <Box sx={styles.loading}>
          <CircularProgress size={24} />
        </Box>
      </DialogContent>
    )
  }
  return (
    <DialogContent>
      {isLoading && (
        <Box sx={styles.loading}>
          <CircularProgress size={24} />
        </Box>
      )}
      <Box sx={styles.resultsLabel}>
        <Typography typography="medium" color="gray.64">
          {t('checkIn:result', { count: clients?.length })}
        </Typography>
        {isRefetchingInBackground && <CircularProgress size={24} />}
      </Box>

      {clients?.map((user) => {
        const { id, email, firstName, lastName, siteInformation, profile } = user.row

        return (
          <SearchResultUserCard
            key={id}
            clientId={id}
            firstName={firstName}
            lastName={lastName}
            email={email}
            profileImageURL={profile}
            siteInformation={siteInformation}
            onClickManualCheckin={handleManualCheckin}
          />
        )
      })}
      {hasNextPage && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: 2,
          }}
        >
          <Button
            variant={BUTTON_VARIANTS.OUTLINED}
            {...(!isFetchingNextPage && {
              startIcon: AngleDownIcon,
            })}
            size={BUTTON_SIZE.MEDIUM}
            theme={BUTTON_THEME.GRAY}
            label={isFetchingNextPage ? <CircularProgress size={20} /> : t('common:loadMore')}
            disabled={isFetchingNextPage}
            onClick={loadMore}
          />
        </Box>
      )}
    </DialogContent>
  )
}

ManualCheckIn.propTypes = {
  searchValue: PropTypes.string,
  onManualCheckin: PropTypes.func,
  isTanning: PropTypes.bool,
}

ManualCheckIn.defaultProps = {
  searchValue: '',
  isTanning: false,
}

export default ManualCheckIn
